<script>
import {
  COMPOSER_CHAT,
  DESTINO_ANEXO_LABEL,
} from '@modules/aawz-intelligence-v2/constants/TextChat'
import {
  ACEITA,
  MAX_ANEXOS,
} from '@modules/aawz-intelligence-v2/constants/Anexos'
import { chaveDoArquivo } from '@modules/aawz-intelligence-v2/utils/chave-de-arquivo'
import { STATUS_UPLOAD } from '@modules/aawz-intelligence-v2/components/send-message/status-de-upload'
import {
  canalDoArquivo,
  CANAL_CARTEIRA,
  CANAL_TRANSCRICAO,
} from '@modules/aawz-intelligence-v2/utils/canal-do-anexo'
import { formatFileSize } from '@utils/Format'
import {
  getAttachmentIcon,
  isImageAttachment,
} from '@modules/tarefas/utils/attachments'
// [feedback do usuário] Mesmo lightbox que os comentários de tarefa já usam
// pra imagem ampliada — BaseModal do design system, e já espera URL local
// (mesmo shape do nosso `item.preview`, um object URL). Sem fetch remoto
// nenhum, então não precisamos do `downloading`: a imagem já está em memória.
import TaskCommentAttachmentViewer from '@modules/tarefas/components/task-comment-attachment-viewer/task-comment-attachment-viewer.vue'

// Lado da miniatura. 72px é o que cabe numa faixa acima de um campo de 52px sem
// a caixa virar painel — e é grande o bastante para reconhecer um print, que é
// o caso que trouxe o recurso.
const LADO_DA_MINIATURA = 72

// [SHOULD, review spec-011 G-3 / precedente S-4 deste arquivo] O ícone do
// selo visível de destino — mesmo racional do S-4 (erro só em `title` era
// invisível para teclado/leitor de tela): o destino (carteira × transcrição)
// só existia em `title`/`alt`/`aria-label`, então quem não passa o mouse
// nunca sabia pra onde o anexo ia. `DESTINO_ANEXO_LABEL` (texto) já é
// compartilhado com o `title`; o ícone é só deste componente porque nenhum
// outro consumidor do canal precisa de um glifo.
const ICONE_POR_CANAL = {
  [CANAL_CARTEIRA]: 'wallet',
  [CANAL_TRANSCRICAO]: 'microphone',
}

/**
 * A fila de anexos do composer — TODOS em miniatura, mesma caixa 72×72.
 *
 * [feedback do usuário] Documento (PDF/Word/planilha/etc) usava chip — pílula
 * com nome+tamanho, sem preview. Virou a MESMA caixa da imagem: ícone do tipo
 * (`AAWZ_DRIVE_FILE_ICONS_MAP`, via `getAttachmentIcon`) no canto superior
 * esquerdo, borda e gradiente de baixo pra cima na cor daquele ícone —
 * reconhecimento pela COR do tipo de arquivo, não mais só pelo nome truncado
 * de uma pílula.
 *
 * ⚠️ O object URL da miniatura NÃO é coletado sozinho. `syncPreviews` revoga o
 * que saiu da lista e o `beforeDestroy` revoga o resto; sem os dois, trocar de
 * anexo vaza uma URL por troca. Mesmo desenho (e mesma `fileKey` estável) do
 * `task-comment-attachment-picker` do módulo de tarefas — foi de lá que este
 * padrão veio, junto com o `handlePaste` que hoje mora em
 * `utils/imagens-do-clipboard`.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila).
 */
export default {
  name: 'IntelligenceV2ComposerAttachments',
  components: { TaskCommentAttachmentViewer },
  props: {
    // `[{ name, size, type }]` — o próprio `File` do browser satisfaz.
    arquivos: {
      type: Array,
      default: function () {
        return []
      },
    },
    // Gap G3: enquanto o envio não tiver canal para arquivo, o gatilho nasce
    // desabilitado. Quem decide é `utils/anexos-liberados.js` (spec-003
    // T306), pela funcionalidade do escritório.
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Só mostra — sem seletor e sem remover.
     *
     * ★ É o que permite a BOLHA da mensagem reusar esta fila em vez de copiar a
     * miniatura. O que não se pode duplicar é o ciclo do object URL: uma segunda
     * implementação do `createObjectURL` sem os três revokes vaza uma URL por
     * imagem enviada, e o vazamento é mudo.
     *
     * ⚠️ O nome do componente ficou estreito (ele não serve só ao composer).
     * Renomear atravessa o teste e as quatro superfícies que o montam — fica
     * anotado, não feito.
     */
    somenteLeitura: {
      type: Boolean,
      default: false,
    },
    /**
     * Progresso do upload real (spec-016 T023) — `{ [chaveDoArquivo]: status }`.
     * `status` é `'enviando' | 'erro'` (qualquer outro valor, incluindo
     * ausência de chave, não desenha nada — o default é "sem novidade",
     * não "com problema").
     *
     * ★ Quem mantém o mapa é o HOSPEDEIRO (`send-message.vue`, via o mixin
     * de upload) — este componente só DESENHA o que recebe, sem saber que
     * upload existe. A chave usada aqui é a MESMA de `chaveDoArquivo`
     * (import compartilhado) — divergir faria o spinner nunca casar com a
     * miniatura certa.
     */
    statusPorChave: {
      type: Object,
      default: () => ({}),
    },
    /**
     * [Decisão G-4] chave → mensagem REAL do backend (400/403), ou o texto
     * genérico quando o mixin não tinha uma (rede/exception) — nunca
     * engolida. Só é lida para itens com `status === 'erro'`; fallback para
     * `rotuloErroUpload` cobre o caso de o mapa não trazer a chave (ex.:
     * consumidor antigo que ainda não passa esta prop).
     */
    mensagemErroPorChave: {
      type: Object,
      default: () => ({}),
    },
    /**
     * [spec-008 T028] A fila é de ENTREGAS do agente, não de anexos do
     * gestor: o clique BAIXA (emite `baixar` com o índice) em vez de abrir a
     * imagem ampliada ou salvar o object URL local.
     *
     * ★ Default `false` — as duas montagens que a bolha do USUÁRIO já faz
     * (`chat-message.vue`, anexo ao vivo e persistido) não passam a prop e
     * não mudam de comportamento em nada.
     *
     * ⚠️ Ligar isto também diz que os itens NÃO têm bytes em memória (é
     * metadado do servidor): `ehSemBytesNaMemoria` passa a valer para a fila
     * inteira, e nenhum object URL é criado — `createObjectURL` sobre um
     * objeto que não é `Blob` lança `TypeError` num browser real (o jsdom
     * não valida o argumento, então o teste sozinho não pegaria).
     */
    comDownload: {
      type: Boolean,
      default: false,
    },
    /**
     * [spec-008 T028] Loading POR ITEM do download — `{[chaveDoArquivo]: true}`,
     * mantido pelo HOSPEDEIRO, mesma divisão de trabalho de `statusPorChave`
     * (este componente só DESENHA).
     *
     * ⚠️ Mapa, nunca um booleano único para a lista: com duas entregas na
     * mesma bolha, um booleano acenderia o spinner nas duas e travaria o
     * clique da que ninguém pediu. A chave é a de `chaveDoArquivo` (import
     * compartilhado) — divergir faria o spinner nunca casar com a miniatura
     * certa, o mesmo defeito já registrado em `statusPorChave`.
     */
    baixandoPorChave: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      // `{ [fileKey]: objectURL }` — só para imagem.
      previews: {},
      ACEITA,
      // `{ url, fileName }` da imagem aberta no viewer — o `@download` do
      // `TaskCommentAttachmentViewer` não devolve payload (ele guarda url/
      // fileName só pra si), então quem baixa precisa da própria cópia.
      imagemAmpliada: null,
    }
  },
  computed: {
    rotuloRemover() {
      return COMPOSER_CHAT.removerAnexo
    },
    rotuloErroUpload() {
      return COMPOSER_CHAT.erroUploadAnexo
    },
    // Tamanho, tipo e ícone já resolvidos: chamada de função no template
    // reexecuta a cada render de qualquer outra prop (regra do repo).
    itens() {
      return this.arquivos.map((arquivo, indice) => {
        const ehImagem = isImageAttachment({
          mimeType: arquivo.type,
          fileName: arquivo.name,
        })
        const icone = getAttachmentIcon({
          fileName: arquivo.name,
          mimeType: arquivo.type,
        })
        // A caixa em si (72×72 sempre) + a identidade visual do documento
        // (borda e gradiente de baixo pra cima na cor do ícone — `33`/`00`
        // são alpha hex, 20%→0%, pra não virar um bloco sólido competindo
        // com o ícone). Imagem não usa borderColor/background: a própria
        // foto ocupa a caixa inteira. Calculado aqui (não no template) —
        // regra do repo: sem lógica no `<template>`.
        const estiloMiniatura = {
          width: this.ladoDaMiniatura + 'px',
          height: this.ladoDaMiniatura + 'px',
        }
        if (!ehImagem) {
          estiloMiniatura.borderColor = icone.color + '66'
          estiloMiniatura.background =
            'linear-gradient(to top, ' +
            icone.color +
            '33 0%, ' +
            icone.color +
            '00 65%)'
        }
        // [T004a — Lote de front A] Nome do canal para onde o anexo está
        // indo (carteira × transcrição), pela MESMA régua de extensão que o
        // upload real usa (`utils/canal-do-anexo.js`) — nunca uma segunda
        // decisão redigitada aqui. Extensão sem canal (`.gif`/`.pptx`, gap
        // conhecido) devolve `''`: sem destino para nomear, o title cai só
        // no nome, sem travessão órfão.
        const canal = canalDoArquivo(arquivo)
        // ⚠️ [spec-008 T028] O destino é para onde o anexo SOBE (carteira ×
        // transcrição). Uma ENTREGA desce, e `canalDoArquivo` decide por
        // EXTENSÃO: sem esta guarda, uma planilha entregue pelo agente
        // ganharia o selo "carteira" — afirmação falsa sobre um arquivo que
        // nunca foi para lá.
        const destino = this.comDownload
          ? ''
          : DESTINO_ANEXO_LABEL[canal] || ''
        const status = this.statusPorChave[this.chaveDoArquivo(arquivo)] || null
        // [spec-008 T028] Loading POR ITEM — só o que o hospedeiro marcou.
        const baixando = Boolean(
          this.baixandoPorChave[this.chaveDoArquivo(arquivo)]
        )
        return {
          chave: this.chaveDoArquivo(arquivo) + '-' + indice,
          indice: indice,
          nome: arquivo.name,
          destino: destino,
          // [SHOULD, review spec-011 G-3] o ícone do selo VISÍVEL do destino
          // (ver template — `.composer-attachments__destino`). `''` quando o
          // canal é desconhecido: some junto com `destino` (mesmo `v-if`).
          iconeDestino: ICONE_POR_CANAL[canal] || '',
          // Owner: "só aparece no chat os arquivos de imagens" — o nome
          // sozinho não dizia para ONDE o anexo ia. Vira o `title`/`alt`/
          // `aria-label` da miniatura (imagem e documento), nas DUAS
          // superfícies que reusam este componente (composer ao vivo e a
          // bolha, `somente-leitura`).
          tituloComDestino: destino ? arquivo.name + ' · ' + destino : arquivo.name,
          // [spec-008 T028] O nome acessível da miniatura. Em modo ENTREGA o
          // clique BAIXA, e "nome — carteira" descreveria a viagem contrária
          // (o destino de um anexo SUBINDO). Fora dele nada muda.
          rotuloDeAcesso: this.comDownload
            ? COMPOSER_CHAT.baixarArquivo(arquivo.name)
            : destino
            ? arquivo.name + ' · ' + destino
            : arquivo.name,
          baixando: baixando,
          tamanho: formatFileSize(arquivo.size),
          // [S-3] Persistido (reaberto após F5, sem bytes reais — ver
          // `utils/anexo-persistido-para-item.js`) nunca é "imagem com
          // preview": sem `preview` a miniatura cai no ícone do tipo, mesmo
          // quando o `content_type` é `image/*`.
          ehImagem: ehImagem && !this.ehSemBytesNaMemoria(arquivo),
          // Object URL de TODOS agora — não só imagem. É o que a miniatura de
          // documento precisa pra baixar ao clicar (não tem preview visual,
          // mas o arquivo já está em memória do mesmo jeito). Persistido não
          // tem bytes — `sincronizarPreviews` nunca cria object URL pra ele,
          // então `preview` fica vazio de propósito (sem clique, sem download).
          preview: this.previews[this.chaveDoArquivo(arquivo)] || '',
          icone: icone,
          estiloMiniatura: estiloMiniatura,
          // `null` por padrão (ver doc da prop): ausência não é "com
          // problema". Só `'enviando'`/`'erro'` desenham algo sobre a caixa.
          status: status,
          // Pré-computado: o template não compara string (vocabulário em
          // `STATUS_UPLOAD`, o mesmo do mixin que mantém o mapa).
          enviando: status === STATUS_UPLOAD.ENVIANDO,
          comErro: status === STATUS_UPLOAD.ERRO,
          // [Decisão G-4] a mensagem REAL do backend, item a item — cai no
          // texto genérico só quando o mapa não trouxe nada para esta chave.
          mensagemDeErro:
            this.mensagemErroPorChave[this.chaveDoArquivo(arquivo)] ||
            this.rotuloErroUpload,
        }
      })
    },
    temItens() {
      return this.itens.length > 0
    },
    // Cheio também bloqueia: abrir para descartar o excedente depois é a mesma
    // promessa quebrada, em escala menor.
    seletorBloqueado() {
      return this.disabled || this.itens.length >= MAX_ANEXOS
    },
    ladoDaMiniatura() {
      return LADO_DA_MINIATURA
    },
  },
  watch: {
    arquivos: {
      handler: 'sincronizarPreviews',
      immediate: true,
    },
  },
  beforeDestroy() {
    this.revogarTodas()
  },
  methods: {
    /**
     * Identidade estável de um `File` — `utils/chave-de-arquivo.js`.
     *
     * Sem ela o object URL seria recriado a cada render — e o anterior, perdido
     * sem revogar. Extraída para módulo compartilhado (spec-016 T023): é a
     * MESMA chave que `send-message-anexos-upload-mixin.js` usa em
     * `statusPorChave` — as duas precisam concordar, senão o spinner de
     * upload nunca acha a miniatura certa.
     */
    chaveDoArquivo(arquivo) {
      return chaveDoArquivo(arquivo)
    },
    /**
     * O item é METADADO, sem bytes em memória?
     *
     * [S-3] Anexo PERSISTIDO (`utils/anexo-persistido-para-item.js`) não é um
     * `File`/`Blob` de verdade — é metadado do bloco técnico, sem bytes.
     * `URL.createObjectURL` sobre um objeto assim lança `TypeError` num
     * browser real (jsdom não valida o argumento, então o teste não pegaria
     * sozinho). O discriminante é `upload_id`: nenhum `File` do browser
     * carrega esse campo — só o adaptador o adiciona.
     *
     * ★ [spec-008 T028] A fila de ENTREGAS (`com-download`) é metadado do
     * servidor inteira — os bytes só chegam no clique, em base64. Por isso a
     * prop responde pela fila toda aqui, e o nome deixou de dizer
     * "persistido": o que os dois casos têm em comum é a AUSÊNCIA de bytes,
     * não a origem.
     */
    ehSemBytesNaMemoria(arquivo) {
      if (this.comDownload) return true
      return Boolean(arquivo && arquivo.upload_id)
    },
    // Cria o que falta e revoga o que saiu — os dois no mesmo lugar, senão um
    // dos dois é esquecido.
    //
    // [feedback do usuário] Todo arquivo ganha object URL agora, não só
    // imagem — documento também é miniatura clicável, e clicar nele baixa o
    // arquivo que já está em memória (sem isto, `abrirAmpliada` não teria de
    // onde tirar a URL pro download). Persistido é a exceção: sem bytes,
    // não há o que passar pro `createObjectURL` — a miniatura degrada pro
    // ícone do tipo, sem preview nem clique (ver `ehSemBytesNaMemoria`).
    sincronizarPreviews() {
      const novas = {}
      this.arquivos.forEach((arquivo) => {
        const chave = this.chaveDoArquivo(arquivo)
        if (this.ehSemBytesNaMemoria(arquivo)) {
          novas[chave] = ''
          return
        }
        novas[chave] = this.previews[chave] || URL.createObjectURL(arquivo)
      })
      Object.keys(this.previews).forEach((chave) => {
        if (!novas[chave]) URL.revokeObjectURL(this.previews[chave])
      })
      this.previews = novas
    },
    revogarTodas() {
      Object.keys(this.previews).forEach((chave) => {
        URL.revokeObjectURL(this.previews[chave])
      })
      this.previews = {}
    },
    abrirSeletor() {
      if (this.seletorBloqueado) return
      const input = this.$refs.input
      if (input) input.click()
    },
    onSelecionar(event) {
      const alvo = event && event.target
      const lista = alvo && alvo.files
      if (lista && lista.length) this.$emit('add', lista)
      // Zerar o input: sem isso, o MESMO arquivo duas vezes seguidas não
      // dispara `change` e o clique parece ter falhado.
      if (alvo) alvo.value = ''
    },
    remover(indice) {
      this.$emit('remove', indice)
    },
    // [feedback do usuário] A miniatura era só reconhecimento (72×72,
    // `object-fit: cover`, corta o print) — sem jeito de ver a imagem
    // inteira. Imagem abre no MESMO viewer que os comentários de tarefa
    // usam; documento não tem preview possível aqui dentro, então clicar
    // baixa direto — a ação que faz sentido pra quem clicou num ícone de PDF.
    aoClicarNaMiniatura(item) {
      if (!item) return
      // [spec-008 T028] Modo ENTREGA: não há bytes aqui, então não há preview
      // nem object URL — quem baixa é o HOSPEDEIRO, que fala com o servidor.
      // O índice é o mesmo contrato de `remover` (o `itens` é decorado; o
      // objeto original fica com quem passou a prop).
      if (this.comDownload) {
        if (item.baixando) return
        this.$emit('baixar', item.indice)
        return
      }
      if (!item.preview) return
      if (item.ehImagem) {
        this.imagemAmpliada = { url: item.preview, fileName: item.nome }
        this.$refs.viewer.open(this.imagemAmpliada)
        return
      }
      this.baixarArquivo({ url: item.preview, fileName: item.nome })
    },
    // Sem loading: `item.preview` já é um object URL na memória (criado em
    // `sincronizarPreviews`), nenhum fetch de rede pra esperar — diferente do
    // comentário de tarefa, que baixa base64 do servidor antes de oferecer o
    // arquivo.
    baixarAmpliada() {
      this.baixarArquivo(this.imagemAmpliada)
    },
    baixarArquivo(anexo) {
      if (!anexo) return
      const link = document.createElement('a')
      link.href = anexo.url
      link.download = anexo.fileName || 'arquivo'
      link.click()
    },
  },
}
</script>

<template>
  <div class="composer-attachments">
    <!-- ⚠️ `input` cru, e não `upload-file-v2`: o componente do DS é
         single-file, sem miniatura e sem lista com remoção individual. Cai na
         exceção que o `CLAUDE.md` do repo declara ("genuinely new interaction
         pattern"), e é o mesmo caminho que o picker de anexos de tarefa segue,
         já aprovado em review. -->
    <input
      v-if="!somenteLeitura"
      ref="input"
      type="file"
      multiple
      :accept="ACEITA"
      class="composer-attachments__input"
      :disabled="seletorBloqueado"
      @change="onSelecionar"
    />

    <!-- A lista carrega o próprio respiro. Ele NÃO pode viver no call-site: ali
         o padding existia mesmo com a fila vazia e empurrava o "+" para dentro
         da caixa — foi o que obrigou a esconder a prévia inteira no modo limpo. -->
    <ul v-if="temItens" class="composer-attachments__lista">
      <li
        v-for="item in itens"
        :key="item.chave"
        class="composer-attachments__item"
      >
        <div
          class="composer-attachments__miniatura"
          :class="{ 'composer-attachments__miniatura--documento': !item.ehImagem }"
          :style="item.estiloMiniatura"
        >
          <img
            v-if="item.ehImagem"
            :src="item.preview"
            :alt="item.tituloComDestino"
            :title="item.tituloComDestino"
            class="composer-attachments__img"
            @click="aoClicarNaMiniatura(item)"
          />
          <!-- Documento: sem preview possível aqui — o ícone do tipo (canto
               superior esquerdo) é o reconhecimento, a cor dele é quem diz
               "isto é um PDF"/"isto é uma planilha". Clique baixa o arquivo. -->
          <button
            v-else
            type="button"
            class="composer-attachments__doc-gatilho"
            :aria-label="item.rotuloDeAcesso"
            :title="item.rotuloDeAcesso"
            :disabled="item.baixando"
            @click="aoClicarNaMiniatura(item)"
          >
            <icon-svg
              :name="item.icone.name"
              :size="18"
              :color="item.icone.color"
              class="composer-attachments__doc-icone"
            />
          </button>
          <!-- [spec-008 T028] Affordance do download: sem ela a miniatura da
               ENTREGA é idêntica à do anexo que o gestor mandou, e nada na
               tela diz que aquilo é clicável para baixar (o `title` só
               aparece no hover do mouse). Some enquanto o item baixa — ali
               quem fala é o spinner. -->
          <div
            v-if="comDownload && !item.baixando"
            class="composer-attachments__selo-download"
            aria-hidden="true"
          >
            <icon-svg name="download" :size="10" color="var(--neutral-900)" />
          </div>
          <!-- [spec-008 T028] Loading POR ITEM. Cobre a caixa inteira pelo
               MESMO motivo do upload logo acima: não se clica duas vezes num
               arquivo que já está sendo baixado. -->
          <div
            v-if="item.baixando"
            class="composer-attachments__status-baixando"
            data-teste="anexo-baixando"
          >
            <v-progress-circular indeterminate size="20" width="2" color="white" />
          </div>
          <!-- Upload real (spec-016 T023). Cobre a caixa inteira — a pessoa
               não deveria clicar/baixar um arquivo que ainda está subindo. -->
          <div
            v-if="item.enviando"
            class="composer-attachments__status-enviando"
          >
            <v-progress-circular indeterminate size="20" width="2" color="white" />
          </div>
          <!-- Erro NÃO cobre a caixa — o arquivo já está em memória (o preview
               continua clicável) e o consultor pode querer olhar/baixar antes
               de decidir remover. Só o selo no canto avisa.
               [S-4, design-conformance 05/09/2026] A mensagem só vivia em
               `:title` — invisível para quem navega por teclado/leitor de
               tela. `role="alert"` + `aria-label` é o MESMO precedente do
               bloco de erro do depósito automático
               (`fee-fixo-guia-simulacao-card.vue`). `:title` fica, para o
               hover do mouse. -->
          <div
            v-if="item.comErro"
            class="composer-attachments__status-erro"
            role="alert"
            :title="item.mensagemDeErro"
            :aria-label="item.mensagemDeErro"
          >
            <icon-svg name="mdi-alert-circle" :size="12" color="var(--danger)" />
          </div>
          <button
            v-if="!somenteLeitura"
            type="button"
            class="composer-attachments__remover composer-attachments__remover--sobreposto"
            :aria-label="rotuloRemover"
            :title="rotuloRemover"
            @click="remover(item.indice)"
          >
            <icon-svg name="close" :size="10" color="var(--neutral-900)" />
          </button>
        </div>
        <!-- [SHOULD, review spec-011 G-3] O destino (carteira × transcrição)
             morava só em `title`/`alt`/`aria-label` — invisível sem passar o
             mouse (mesmo precedente do S-4 acima, para a mensagem de erro).
             Selo abaixo da miniatura, fora dela: não compete com os overlays
             (remover/erro/enviando), que já ocupam os 4 cantos da caixa. -->
        <div
          v-if="item.destino"
          class="composer-attachments__destino ia-texto-micro"
          :title="item.tituloComDestino"
        >
          <icon-svg :name="item.iconeDestino" :size="10" color="var(--neutral-600)" />
          <span>{{ item.destino }}</span>
        </div>
      </li>
    </ul>

    <!-- Um viewer só pra fila inteira — abre com a imagem clicada, via
         `abrirAmpliada`. Não precisa de `v-if`: o BaseModal por trás já
         controla a própria visibilidade pelo `busModal`. -->
    <TaskCommentAttachmentViewer ref="viewer" @download="baixarAmpliada" />
  </div>
</template>

<style lang="sass" scoped>
.composer-attachments__input
  // O gatilho visível é o "+" do composer, via `abrirSeletor()`.
  display: none

.composer-attachments__lista
  display: flex
  flex-wrap: wrap
  align-items: center
  gap: 6px
  margin: 0
  // O respiro da faixa mora aqui — ver o comentário no template. VERTICAL só:
  // o recuo horizontal é o da caixa do composer, e repeti-lo aqui afastaria a
  // miniatura da borda que o "+" respeita logo abaixo.
  padding: 8px 0 4px
  list-style: none

.composer-attachments__item
  display: flex
  // Coluna, não linha: o selo de destino (`__destino`) vive ABAIXO da
  // miniatura, não ao lado — a caixa continua 72×72 sozinha.
  flex-direction: column
  align-items: center
  gap: 2px

.composer-attachments__destino
  color: var(--neutral-600)
  display: flex
  align-items: center
  gap: 2px
  // Acompanha a largura da miniatura (72px) — nome do canal é sempre curto
  // ("carteira"/"transcrição"), então não deveria precisar truncar, mas o
  // teto protege contra um canal futuro com rótulo mais longo.
  max-width: 72px
  white-space: nowrap
  overflow: hidden
  text-overflow: ellipsis
  // [S-4, mesmo racional do selo de erro] Rótulo em minúsculas no dado
  // (`DESTINO_ANEXO_LABEL`, compartilhado com o `title`) — só a CAPITAL
  // inicial é cosmética, via CSS, pra não duplicar o texto em duas formas.
  text-transform: capitalize

.composer-attachments__miniatura
  position: relative
  overflow: hidden
  border: 1px solid var(--border-hairline)
  // Acompanha a caixa do composer (24px numa altura de 52px) em vez do
  // `--radius-md` de 8px, que num quadrado de 72px lia como card e não como
  // parte do campo. ⚠️ Não é o MESMO número: 24px num quadrado de 72 vira quase
  // um círculo. `--radius-card` (14px) é a mesma proporção de curvatura.
  border-radius: var(--radius-card)
  background: var(--neutral-100)

.composer-attachments__img
  width: 100%
  height: 100%
  // `cover` e não `contain`: a miniatura é reconhecimento, não leitura — barra
  // branca em volta de um print vertical desperdiça os 72px que ela tem.
  object-fit: cover
  display: block
  // [feedback do usuário] Clicável — abre a imagem ampliada no viewer.
  cursor: pointer

// [feedback do usuário] Documento também é miniatura 72×72, não mais chip —
// `border-color`/`background` vêm por prop (inline style, `item.estiloMiniatura`
// no script), na cor do TIPO do arquivo (`AAWZ_DRIVE_FILE_ICONS_MAP`). O CSS
// aqui só marca que a borda desta variante é mais grossa que a de imagem —
// numa borda de 1px a cor do gradiente quase some.
.composer-attachments__miniatura--documento
  border-width: 1.5px

.composer-attachments__doc-gatilho
  width: 100%
  height: 100%
  display: flex
  // Canto superior esquerdo — é o pedido: reconhecimento pelo ícone ali, não
  // centralizado como um selo.
  align-items: flex-start
  justify-content: flex-start
  // [feedback do usuário] 1px a menos que os outros dois lados — o ícone
  // sobe e vai pra esquerda um tico, sem descolar da borda.
  padding: 5px 6px 6px 5px
  border: 0
  background: none
  cursor: pointer

.composer-attachments__remover
  display: inline-flex
  align-items: center
  justify-content: center
  width: 16px
  height: 16px
  border: 0
  border-radius: 50%
  background: none
  cursor: pointer
  &:hover
    background: var(--neutral-200)

// Sobre a imagem o botão precisa de fundo próprio: sem ele o "x" some num print
// claro e vira ruído num escuro.
.composer-attachments__remover--sobreposto
  position: absolute
  top: 4px
  right: 4px
  background: rgba(255, 255, 255, 0.88)
  &:hover
    background: var(--white)

// Upload real (spec-016 T023). Cobre a caixa inteira em vez de um selo — o
// arquivo ainda não existe no servidor, então nem preview nem download fazem
// sentido clicáveis enquanto o spinner gira.
// [spec-008 T028] O DOWNLOAD entra na MESMA declaração: o fato desenhado é
// "este item está ocupado", e o sentido da viagem não muda o desenho. Duas
// regras separadas seriam duas gramáticas para o mesmo estado — e o segundo
// `rgba()` escrito à mão divergiria do primeiro na primeira troca de tom.
.composer-attachments__status-enviando,
.composer-attachments__status-baixando
  position: absolute
  inset: 0
  display: flex
  align-items: center
  justify-content: center
  background: rgba(0, 0, 0, 0.45)
  border-radius: inherit

// Erro NÃO cobre a caixa (ver comentário no template) — só o selo no canto
// oposto ao botão de remover, para os dois não competirem pelo mesmo canto.
.composer-attachments__status-erro,
.composer-attachments__selo-download
  position: absolute
  bottom: 4px
  display: inline-flex
  align-items: center
  justify-content: center
  width: 16px
  height: 16px
  border-radius: 50%
  background: var(--white)

.composer-attachments__status-erro
  left: 4px

// [spec-008 T028] O selo do download ocupa o canto OPOSTO ao do erro (embaixo
// à esquerda) e ao do remover (em cima à direita) — os três nunca disputam o
// mesmo canto. `pointer-events: none` porque quem recebe o clique é o gatilho
// da miniatura, por baixo dele.
.composer-attachments__selo-download
  right: 4px
  pointer-events: none
</style>
